import { Room, RoomEvent, Track } from 'livekit-client';

/**
 * The room behind a claimed support call — voice, chat, transcript and the
 * assistant sitting beside the agent.
 *
 * Deliberately its own Room, not the copilot's. `livekitRoom.js` keeps a
 * module-level singleton, so connecting a second room through it tears the
 * first one down — which is exactly what app_admin worked around by hiding its
 * chat widget on the agent console. An agent taking a call should not lose the
 * assistant, so support gets its own connection and the two coexist.
 *
 * THE WIRE PROTOCOL, which is the part that has to match the agent worker
 * exactly (it is app_admin's `livekitService`, kept to the letter):
 *
 *   in   lk.transcription  data    {speaker, transcript|text, timestamp, is_final}
 *   in   lk.chat           text    the customer's typed line (raw, or {text})
 *   in   ai_reply_for_human_agent  the assistant's suggestion to the agent
 *        advisor_suggestion        — the legacy name, still emitted; accept both
 *   out  lk.transcription  data    {text, transcript, role:'human_agent'}
 *                                  → what the CUSTOMER sees. publishData, not
 *                                    sendText: the customer switches on `role`.
 *   out  lk.chat           text    'lk.chat ' + line → the AI, not the customer
 *   out  ask_ai            data    {text, ...context}
 *   out  control           data    {action: handover_to_ai | reassign | close}
 */

export const TOPICS = Object.freeze({
  TRANSCRIPT: 'lk.transcription',
  CHAT: 'lk.chat',
  AI_REPLY: 'ai_reply_for_human_agent',
  AI_REPLY_LEGACY: 'advisor_suggestion',
  ASK_AI: 'ask_ai',
  CONTROL: 'control',
});

/** Every agent line on the lk.chat stream carries this token. */
const LK_CHAT_PREFIX = 'lk.chat ';

export const CONTROL_ACTIONS = Object.freeze({
  HANDOVER_TO_AI: 'handover_to_ai',
  REASSIGN: 'reassign',
  CLOSE: 'close',
});

let room = null;
const listeners = new Set();
let seq = 0;

const state = {
  status: 'idle', // idle | connecting | connected | failed
  micOn: false,
  participants: 0,
  error: null,
  /* One ordered log of everything said, however it was said, so the agent
     reads a single conversation rather than three panes that have to be
     mentally interleaved. `channel` is what separates them on screen. */
  messages: [],   // { id, channel: 'customer'|'agent'|'transcript', who, text, at, partial }
  suggestions: [], // { id, text, isSummary, at }
};

const emit = () => {
  const snapshot = { ...state, messages: [...state.messages], suggestions: [...state.suggestions] };
  listeners.forEach((fn) => {
    try { fn(snapshot); } catch { /* a listener must not break the room */ }
  });
};

const set = (patch) => { Object.assign(state, patch); emit(); };

const push = (msg) => {
  seq += 1;
  state.messages = [...state.messages, { id: `m${seq}`, at: new Date().toISOString(), ...msg }];
  emit();
};

/** Subscribe to room state. Returns the unsubscribe. */
export function onSupportRoom(fn) {
  listeners.add(fn);
  fn({ ...state, messages: [...state.messages], suggestions: [...state.suggestions] });
  return () => listeners.delete(fn);
}

export const supportRoomState = () => ({ ...state });

const decode = (payload) => {
  try { return JSON.parse(new TextDecoder().decode(payload)); } catch { return null; }
};

/**
 * Attach a remote audio track to the page.
 *
 * Browsers will not play audio until a gesture has happened, and claiming the
 * call is that gesture — `startAudio` runs right after connecting so the
 * customer is audible without a second click.
 */
const attach = (track) => {
  if (track.kind !== Track.Kind.Audio) return;
  const el = track.attach();
  el.style.display = 'none';
  el.setAttribute('data-support-audio', '1');
  document.body.appendChild(el);
};
const detach = (track) => track.detach().forEach((el) => el.remove());

/**
 * A spoken line.
 *
 * An interim transcript replaces the previous interim from the same speaker
 * rather than stacking: speech recognition emits a growing sentence many times
 * a second, and appending each one turns the log into noise.
 */
const onTranscript = (parsed) => {
  const speaker = parsed.speaker || 'customer';
  const text = parsed.transcript || parsed.text || '';
  if (!text) return;
  const isFinal = parsed.is_final !== false;
  const last = state.messages[state.messages.length - 1];
  if (last && last.channel === 'transcript' && last.partial && last.who === speaker) {
    state.messages = [
      ...state.messages.slice(0, -1),
      { ...last, text, partial: !isFinal, at: parsed.timestamp || last.at },
    ];
    emit();
    return;
  }
  push({ channel: 'transcript', who: speaker, text, partial: !isFinal });
};

export async function connectSupportRoom({ token, wsUrl }) {
  if (!token || !wsUrl) throw new Error('A LiveKit token and URL are required');
  await disconnectSupportRoom();

  const next = new Room({ adaptiveStream: true, dynacast: true });
  room = next;
  set({ status: 'connecting', error: null, participants: 0, micOn: false, messages: [], suggestions: [] });

  next
    .on(RoomEvent.TrackSubscribed, attach)
    .on(RoomEvent.TrackUnsubscribed, detach)
    .on(RoomEvent.ParticipantConnected, () => set({ participants: next.numParticipants }))
    .on(RoomEvent.ParticipantDisconnected, () => set({ participants: next.numParticipants }))
    .on(RoomEvent.Disconnected, () => {
      if (room === next) set({ status: 'idle', participants: 0, micOn: false });
    })
    .on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
      const parsed = decode(payload);
      if (!parsed) return;
      if (topic === TOPICS.TRANSCRIPT) {
        /* Our own agent lines come back on this topic; they are already shown. */
        if (parsed.role === 'human_agent') return;
        onTranscript(parsed);
      } else if (topic === TOPICS.AI_REPLY || topic === TOPICS.AI_REPLY_LEGACY) {
        seq += 1;
        state.suggestions = [...state.suggestions, {
          id: parsed.suggestion_id || `s${seq}`,
          text: parsed.text || '',
          isSummary: Boolean(parsed.is_summary),
          at: new Date().toISOString(),
        }];
        emit();
      }
    });

  /* The customer's typed lines arrive as a text stream, not a data packet.
     Registered BEFORE connect so an early message is not missed. */
  try {
    next.registerTextStreamHandler(TOPICS.CHAT, async (reader, info) => {
      let raw;
      try { raw = await reader.readAll(); } catch { return; }
      if (!raw) return;
      let text = raw;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && typeof parsed.text === 'string') text = parsed.text;
      } catch { /* not JSON — a plain line */ }
      if (!text) return;
      /* Our own agent lines echo back on this stream; skip the prefix we set. */
      if (String(text).startsWith(LK_CHAT_PREFIX)) return;
      push({ channel: 'customer', who: info?.identity || 'customer', text });
    });
  } catch { /* an older server without text streams still gives voice */ }

  try {
    await next.connect(wsUrl, token);
    await next.startAudio().catch(() => {});
    await next.localParticipant.setMicrophoneEnabled(true).catch(() => {});
    set({
      status: 'connected',
      participants: next.numParticipants,
      micOn: next.localParticipant.isMicrophoneEnabled,
    });
    return next;
  } catch (err) {
    if (room === next) { room = null; set({ status: 'failed', error: err.message }); }
    throw err;
  }
}

export async function setSupportMic(on) {
  if (!room) return;
  await room.localParticipant.setMicrophoneEnabled(on).catch(() => {});
  set({ micOn: room.localParticipant.isMicrophoneEnabled });
}

/**
 * Say something to the CUSTOMER.
 *
 * publishData on the transcript topic tagged `role: 'human_agent'` — the
 * customer's client switches on that role. Not sendText, which is the AI path.
 */
export async function sendToCustomer(text) {
  const line = String(text || '').trim();
  if (!room || !line) return;
  const payload = new TextEncoder().encode(JSON.stringify({
    text: line, transcript: line, role: 'human_agent',
  }));
  await room.localParticipant.publishData(payload, { topic: TOPICS.TRANSCRIPT, reliable: true });
  push({ channel: 'agent', who: 'you', text: line });
}

/** Say something to the ASSISTANT, which the customer does not see. */
export async function sendToAssistant(text) {
  const line = String(text || '').trim();
  if (!room || !line) return;
  await room.localParticipant.sendText(`${LK_CHAT_PREFIX}${line}`, { topic: TOPICS.CHAT });
  push({ channel: 'internal', who: 'you', text: line });
}

/** Ask the assistant a question about the call. */
export async function askAssistant(text, context = {}) {
  const line = String(text || '').trim();
  if (!room || !line) return;
  const payload = new TextEncoder().encode(JSON.stringify({ text: line, ...context }));
  await room.localParticipant.publishData(payload, { topic: TOPICS.ASK_AI, reliable: true });
  push({ channel: 'internal', who: 'you', text: line });
}

/** Hand back to the AI, reassign, or close the conversation. */
export async function sendControl(action, extra = {}) {
  if (!room) return;
  const payload = new TextEncoder().encode(JSON.stringify({ action, ...extra }));
  await room.localParticipant.publishData(payload, { topic: TOPICS.CONTROL, reliable: true });
}

export async function disconnectSupportRoom() {
  const old = room;
  room = null;
  if (!old) return;
  document.querySelectorAll('[data-support-audio]').forEach((el) => el.remove());
  await old.disconnect().catch(() => {});
  set({
    status: 'idle', participants: 0, micOn: false, error: null, messages: [], suggestions: [],
  });
}
